// Token service to manage Auth0 tokens without using hooks
import { sessionStorage, STORAGE_KEYS } from '../utils/sessionStorage';

// These will be our global token caches
let cachedAccessToken: string | null = null;
let cachedIdToken: string | null = null;

// Function to register both tokens from Auth0
export const registerTokens = (accessToken: string, idToken: string): void => {
  console.log('TokenService: Registering tokens');
  cachedAccessToken = accessToken;
  cachedIdToken = idToken;
  
  // Also store in session storage for backward compatibility
  try {
    // Store the tokens directly without JSON.stringify to avoid parsing issues
    window.sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    window.sessionStorage.setItem(STORAGE_KEYS.ID_TOKEN, idToken);
    // For backward compatibility
    window.sessionStorage.setItem(STORAGE_KEYS.TOKEN, accessToken);
    console.log('TokenService: Tokens stored in session storage directly');
  } catch (error) {
    console.error('TokenService: Error storing tokens in session storage:', error);
  }
  
  console.log('TokenService: cachedAccessToken set to:', cachedAccessToken ? 'Token exists' : 'No token');
  console.log('TokenService: cachedIdToken set to:', cachedIdToken ? 'Token exists' : 'No token');
};

// Legacy function to register a single token (for backward compatibility)
export const registerToken = (token: string): void => {
  console.log('TokenService: Registering single token (legacy method)');
  registerTokens(token, ''); // Empty ID token
};

// Function to get the current access token
export const getAccessToken = (): string | null => {
  console.log('TokenService: Getting access token');
  
  // First try the cached token
  if (cachedAccessToken) {
    console.log('TokenService: Returning cached access token');
    return cachedAccessToken;
  }
  
  // Fall back to session storage
  console.log('TokenService: No cached access token, checking session storage');
  
  // Try the new key first, then fall back to the old key
  const sessionToken = window.sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
                       window.sessionStorage.getItem(STORAGE_KEYS.TOKEN);
  
  console.log('TokenService: Session storage access token:', sessionToken ? 'Token found' : 'No token in session storage');
  
  return sessionToken;
};

// Function to get the current ID token
export const getIdToken = (): string | null => {
  console.log('TokenService: Getting ID token');
  
  // First try the cached token
  if (cachedIdToken) {
    console.log('TokenService: Returning cached ID token');
    return cachedIdToken;
  }
  
  // Fall back to session storage
  console.log('TokenService: No cached ID token, checking session storage');
  
  const sessionToken = window.sessionStorage.getItem(STORAGE_KEYS.ID_TOKEN);
  console.log('TokenService: Session storage ID token:', sessionToken ? 'Token found' : 'No token in session storage');
  
  return sessionToken;
};

// Legacy function to get the token (for backward compatibility)
export const getToken = (): string | null => {
  console.log('TokenService: Getting token (legacy method)');
  return getAccessToken();
};

// Function to clear all tokens
export const clearTokens = (): void => {
  console.log('TokenService: Clearing all tokens');
  cachedAccessToken = null;
  cachedIdToken = null;
  
  try {
    // Clear from both our wrapper and directly from window.sessionStorage to be safe
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
    
    window.sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    window.sessionStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
    window.sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
    
    console.log('TokenService: All tokens cleared from session storage');
  } catch (error) {
    console.error('TokenService: Error clearing tokens from session storage:', error);
  }
  
  console.log('TokenService: All tokens cleared');
};

// Legacy function to clear the token (for backward compatibility)
export const clearToken = (): void => {
  console.log('TokenService: Clearing token (legacy method)');
  clearTokens();
};

// Export the token service
export const tokenService = {
  registerTokens,
  registerToken, // For backward compatibility
  getAccessToken,
  getIdToken,
  getToken, // For backward compatibility
  clearTokens,
  clearToken // For backward compatibility
};

export default tokenService;